import styled from '@emotion/styled'
import { faBell, faReply } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Loader from '../../../components/global/Loader'
import SearchInput from '../../../components/global/SearchInput'
import SkillCard from '../../../components/global/SkillCard'
import Title from '../../../components/global/Title'
import FilterStatus from '../../../components/member/FilterStatus'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { apiClient } from '../../../services/apiClient'
import { SkillStatusEnum } from '../../../types/skill.type'
import { UserModel } from '../../../types/user.type'
import { TitleWrapper } from '../../admin/users/AdminUsers'
import { FilterWrapper } from '../../member/MemberMyProgression'
import { SkillsWrapper } from './CoachMyMembers'

const CoachMyMembersSingleMemberView = () => {
    const user = useContext(UserContext)
    const { t } = useTranslation('translation')

    const navigate = useNavigate()
    const { memberId } = useParams()

    const lng = useLangSelected()

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedStatus, setSelectedStatus] = useState<SkillStatusEnum[]>([
        SkillStatusEnum.TO_DISCOVER,
        SkillStatusEnum.STARTED,
        SkillStatusEnum.CONFIRMED,
        SkillStatusEnum.MASTERED,
    ])

    const { data: member } = useQuery({
        queryKey: ['coach-member'],
        queryFn: () => apiClient.get<UserModel>(`/users/${memberId}`).then((res) => res.data),
    })

    const skillsFiltered = useMemo(
        () =>
            member?.skills
                ?.filter((userSkill: any) => `${userSkill.skill.name}`.toLowerCase().includes(searchTerm.toLowerCase()))
                .filter((userSkill: any) => selectedStatus.includes(userSkill.status)) ?? [],
        [searchTerm, member, selectedStatus],
    )

    if (!member) return <Loader />

    return (
        <div>
            <TitleWrapper>
                <div className="d-flex align-items-center">
                    <TitleCustom>
                        {member.firstName} {member.lastName}
                        <SessionLink to={`/${lng}/bva-coach/seances`} title={t('coach.view-sessions')}>
                            <FontAwesomeIcon icon={faReply} /> {t('coach.my-sessions')}
                        </SessionLink>
                        {member.notifySkillChanges && (
                            <NotificationIcon icon={faBell} title={t('member.notify-skill-changes')} />
                        )}
                    </TitleCustom>
                </div>
                <StyledButtonLink to={`/${lng}/bva-coach/mes-membres`} className="btn btn-primary mb-2">
                    {t('global.back')}
                </StyledButtonLink>
            </TitleWrapper>
            <FilterWrapper>
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('member.search-skill-input')}
                />
                <FilterStatus selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
            </FilterWrapper>
            <SkillsWrapper>
                {skillsFiltered.map((userSkill: any) => (
                    <SkillCard
                        key={userSkill.id}
                        id={userSkill.id}
                        name={userSkill.skill.name}
                        status={userSkill.status}
                        comment={userSkill.comment}
                        redirect={() => navigate(`/${lng}/bva-coach/mes-membres/${member.id}/${userSkill.id}`)}
                    />
                )) ?? ''}
            </SkillsWrapper>
        </div>
    )
}

const TitleCustom = styled(Title)`
    display: flex;
    align-items: center;
`

const NotificationIcon = styled(FontAwesomeIcon)`
    color: #ffc107;
    margin-left: 16px;
    font-size: inherit;
`

const SessionLink = styled(Link)`
    display: flex;
    align-items: center;
    color: rgb(48, 128, 197);
    margin-left: 16px;
    text-decoration: none;
    font-size: 0.9em;
    transition: color 0.2s;

    & svg {
        margin-right: 8px;
    }

    &:hover {
        text-decoration: underline;
    }
`

export default CoachMyMembersSingleMemberView
